import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import z from 'zod'
import { useAuthenticator } from '~/features/auth'
import { useTRPC } from '~/services/trpc'
import { PublicTemplate } from '~/templates/public'

import {
  Button,
  Form,
  FormErrorAlert,
  FormFieldInput,
  FormFieldSelect,
  processFormError,
} from '@hap/components'

export const Route = createFileRoute('/_authenticated/welcome/organization-details')({
  component: RouteComponent,
})

const formSchema = z.object({
  timezone: z.string(),
  organizationName: z
    .string()
    .min(1, { message: "Please enter your organization's name" })
    .max(100, { message: 'Your organization name must be less than 100 characters' }),
})

const timezones = Intl.supportedValuesOf('timeZone').map((timezone) => ({
  label: timezone.replace(/_/g, ' ').replace('/', ' / '),
  value: timezone,
}))

function RouteComponent() {
  const form = useForm({
    values: {
      organizationName: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    resolver: zodResolver(formSchema),
  })

  const trpc = useTRPC()
  const mutation = useMutation(trpc.organizations.create.mutationOptions())
  const [authenticator] = useAuthenticator()

  const handleSubmit = form.handleSubmit(async (values) => {
    await mutation
      .mutateAsync({
        name: values.organizationName,
        timezone: values.timezone,
      })
      .then(async (response) => {
        await authenticator({
          organizationId: response.organizationId,
          legacyId: response.legacyOrganizationId,
          default: true,
        })
      })
      .catch((error) => {
        processFormError(form.setError, error, {
          genericMessage: 'An error occurred while creating your organization',
        })
      })
  })

  return (
    <PublicTemplate width={400}>
      <section className="bg-accent grid gap-8 p-8">
        <h1 className="text-center text-xl font-semibold">Create your organization</h1>
        <Form {...form}>
          <form onSubmit={handleSubmit} className="grid gap-4">
            <FormErrorAlert />
            <FormFieldInput
              autoFocus
              name="organizationName"
              control={form.control}
              placeholder="Organization name"
              type="text"
              autoComplete="off"
            />

            <FormFieldSelect
              autoFocus
              name="timezone"
              control={form.control}
              label="Timezone"
              options={timezones}
            />

            <Button
              type="submit"
              variant="default"
              loading={form.formState.isSubmitting}
              loadingLabel="Creating organization..."
            >
              Create organization
            </Button>
          </form>
        </Form>
      </section>
    </PublicTemplate>
  )
}
